import { Injectable } from '@angular/core';

import { catchError, Observable, of, switchMap, tap } from 'rxjs';

import { AppType } from '../../../types/enums/app/app-type.enum';
import { ActualAppVersionClient } from '../domain/services/actual-app-version-client';
import { AppActualVersionEnforcer } from '../domain/services/version-check.interface';
import { VersionCheckRequestFactory } from './factory/version-check-request-factory.service';
import { UpdateAppVersionModalPresenter } from './update-app-version-modal-presenter.service';

/**
 * Service for checking if the app needs to be updated
 * Implements the VersionCheckService abstract class
 */
@Injectable({
    providedIn: 'root'
})
export class BackendMobileAppActualVersionEnforcer extends AppActualVersionEnforcer {
    constructor(
        private backendClient: ActualAppVersionClient,
        private versionCheckRequestFactory: VersionCheckRequestFactory,
        private updateAppVersionModalPresenter: UpdateAppVersionModalPresenter
    ) {
        super();
    }

    /**
     * Checks if the app needs to be updated by comparing the current version with the version on the server.
     * If an update is needed, it will show an unclosable modal.
     * @param appType The type of app (client or guard)
     * @returns An Observable that emits true if the app needs to be updated, false otherwise
     */
    execute(appType: AppType): Observable<boolean> {
        // Only check version for mobile apps
        if (!this.versionCheckRequestFactory.isMobileApp()) {
            return of(false);
        }

        return this.versionCheckRequestFactory.createRequest(appType).pipe(
            switchMap(request => {
                // Make API call to check version using the backend client
                return this.backendClient.checkVersion(request);
            }),
            tap(needsUpdate => {
                // If an update is needed, show the modal
                if (needsUpdate) {
                    this.updateAppVersionModalPresenter.showUpdateModal().then();
                }
            }),
            catchError(() => {
                // If there's an error in the process, assume no update is needed
                return of(false);
            })
        );
    }
}
